import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { TasksService } from 'app/modules/admin/apps/task-list/task-list.service';
import { Observable, ReplaySubject, of } from 'rxjs';
import { catchError, filter, map, timeout } from 'rxjs/operators';
import { UserService } from '../user/user.service';

@Injectable({
    providedIn: 'root'
})
export class NavigationService {
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);
    // Navegação atual + última contagem de tasks conhecida, para manter o badge do
    // menu "Tasks" atualizado de forma reativa (mesmo que a 1ª busca falhe/demore).
    private _navigationData: Navigation | null = null;
    private _lastTaskCount: number | null = null;
    // Recontagem ao trocar de tela (ex.: depois de concluir uma tarefa), no máximo 1 a cada 60 s:
    // `/api/task?type=tasks` consulta o Camunda e é pesado em produção.
    private _lastTasksFetch = 0;
    private static readonly TASKS_REFRESH_MS = 60000;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private taskListService: TasksService,
        private userService: UserService,
        private _fuseNavigationService: FuseNavigationService,
        private _router: Router
    ) {
        // Sempre que as tasks carregarem (aqui no nav OU ao abrir a página Tasks),
        // reflete a contagem no badge do menu.
        this.taskListService.tasks$.subscribe((tasks) => {
            if (tasks) {
                this._lastTaskCount = tasks.length;
                this._applyTasksBadge();
            }
        });

        this._router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(() => {
                if (this._navigationData && Date.now() - this._lastTasksFetch > NavigationService.TASKS_REFRESH_MS) {
                    this._fetchTasks();
                }
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        // Dispara a busca de tasks sem travar o menu; o resultado atualiza o badge
        // de forma reativa (via tasks$ no construtor). Se falhar/demorar, o menu já
        // apareceu — o número entra assim que as tasks chegarem.
        // 15s cancelava a busca com frequencia: `/api/task?type=tasks` consulta o
        // Camunda e ainda enriquece cada tarefa, e em producao passa disso sem
        // ser erro. Cancelada, a contagem nunca chegava e o badge ficava com o
        // valor anterior — o que so "se corrigia" ao abrir a tela de Tasks.
        this._fetchTasks();

        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            map((navigation) => {
                const userPermissions = this.userService.getAllUserGroups();
                navigation.default = this.filterNavigationByPermission(navigation.default, userPermissions);
                this._navigationData = navigation;
                // Aplica o badge se já tivermos uma contagem conhecida
                this._applyTasksBadge();
                this._navigation.next(navigation);
                return navigation;
            })
        );
    }

    /**
     * Aplica a contagem de tasks no badge do item "Tasks" da navegação atual
     * e reemite a navegação (para o menu atualizar em tempo real).
     */
    private _applyTasksBadge(): void {
        if (this._lastTaskCount === null || !this._navigationData) {
            return;
        }
        if (!this._navigationData.default.some((item) => item.id === 'tasks')) {
            return;
        }
        // Objeto novo (não mutação): o menu do Fuse é OnPush e só redesenha com input novo. Antes o
        // número chegava, mas só aparecia quando algo forçava o redesenho (ex.: abrir a tela Tasks).
        const badge = {
            title: this._lastTaskCount.toString(),
            classes: 'px-2.5 bg-primary-400 text-black rounded-full'
        };
        this._navigationData = {
            ...this._navigationData,
            default: this._navigationData.default.map((item) => (item.id === 'tasks' ? { ...item, badge } : item)),
        };
        this._navigation.next(this._navigationData);
        // Os itens do menu também são OnPush: refresh() avisa cada um para se redesenhar.
        setTimeout(() => this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation')?.refresh());
    }

    /**
     * Busca as tasks sem travar o menu; o resultado chega pelo tasks$ do construtor.
     * 15s cancelava a busca com frequencia: `/api/task?type=tasks` consulta o Camunda e ainda
     * enriquece cada tarefa, e em producao passa disso sem ser erro.
     */
    private _fetchTasks(): void {
        this._lastTasksFetch = Date.now();
        this.taskListService.getTasks().pipe(
            timeout(60000),
            catchError(() => of(null))
        ).subscribe();
    }

    /**
     * Filter navigation items by permission
     */
    private filterNavigationByPermission(items: any[], permissions: string[]): any[] {
        return items
            .filter(item => {
                if (!item.auth) return true;
                return item.auth.some(auth => {
                    if (auth.endsWith('/*')) {
                        const prefix = auth.slice(0, -2);
                        return permissions.some(p => p === prefix || p.startsWith(prefix + '/'));
                    }
                    return permissions.includes(auth);
                });
            })
            .map(item => ({
                ...item,
                children: item.children ? this.filterNavigationByPermission(item.children, permissions) : undefined
            }));
    }
}
